import { PropsWithChildren, useContext } from 'react';

import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { FormSpy } from 'react-final-form';

import { Button, notify, Stack } from '@cast/design-system';

import { useSimulatedClusterDemoUi } from '@cast/infrastructure-demo';
import { FooterBox } from 'components/boxes';
import { PageFooter } from 'components/portals';
import {
  isManagedByAnnotations,
  PreparedWorkload,
} from 'features/workload-optimization';
import {
  PartialWorkloadUpdateRequest,
  useUpdateWorkloadMutationV2,
} from 'hooks/mutations/workload-optimization/useUpdateWorkloadV2Mutation';
import {
  HorizontalRightsizingFormModel,
  HorizontalRightsizingFormModelV2,
  WorkloadDetailsFormModel,
} from './_types';
import { WorkloadDetailsContext } from '../_context';
import { FixDirtyFormIssue } from '../../_components/form/FixDirtyFormIssue';
import { usePermitEditWorkload } from '../../_hooks/permissions/usePermitEditWorkload';
import { useHpaV2Status, HpaV2Status } from '../../_hooks/useHpaV2Status';

import { RffForm } from '@cast/shared-rff-ui';

/**
 * Base form component for workload rightsizing settings.
 * Handles form state, validation, and submission logic shared between V1 and V2 implementations.
 * Should not be used directly - use RightsizingSettingsForm (V1) or RightsizingSettingsFormV2 (V2).
 */
export const RightsizingSettingsFormBase = <
  T extends
    | WorkloadDetailsFormModel<HorizontalRightsizingFormModel>
    | WorkloadDetailsFormModel<HorizontalRightsizingFormModelV2>
>({
  workload,
  children,
  initialValue,
  validate,
  formModelToApiFn,
}: PropsWithChildren<{
  workload: PreparedWorkload;
  initialValue: T;
  validate: any;
  formModelToApiFn: (args: {
    workload: PreparedWorkload;
    model: T;
    dirtyFields: Record<string, boolean>;
    hpaV2Status: HpaV2Status;
  }) => PartialWorkloadUpdateRequest;
}>) => {
  const isSimulatedClusterDemoUi = useSimulatedClusterDemoUi();

  const { hpaV2Status } = useHpaV2Status();

  const { permitEdit } = usePermitEditWorkload();
  const { isNamespaceWritable } = useContext(WorkloadDetailsContext);

  const { mutate } = useUpdateWorkloadMutationV2();

  return (
    <RffForm
      mode={
        // todo:(mike) mode should be view mode also when isCastware
        permitEdit &&
        !isManagedByAnnotations(workload) &&
        !isSimulatedClusterDemoUi &&
        isNamespaceWritable(workload.namespace)
          ? 'edit'
          : 'view'
      }
      initialValues={initialValue}
      validate={validate}
      mutators={{
        ...arrayMutators,
      }}
      onSubmit={(model: T, form: FormApi<T>) => {
        if (!workload) {
          return;
        }

        mutate(
          formModelToApiFn({
            workload,
            model,
            dirtyFields: form.getState().dirtyFields,
            hpaV2Status,
          }),
          {
            onSuccess: () => {
              notify.success('Changes saved successfully', {
                key: 'succeeded-to-save-workload',
              });
            },
            onError: () =>
              notify.error('Failed to save', {
                key: 'failed-to-save-workload',
              }),
          }
        );
      }}
    >
      <FixDirtyFormIssue />

      <Stack
        direction="column"
        gap={24}
        sx={{
          '& .DS-FormHelperText-text': {
            whiteSpace: 'nowrap',
          },
        }}
      >
        {children}
      </Stack>
      <FormSpy subscription={{ dirty: true }}>
        {({ dirty, form }) =>
          dirty ? (
            <PageFooter>
              <FooterBox>
                <Stack
                  direction="row"
                  ml="auto"
                  my={8}
                  gap={8}
                  width="max-content"
                >
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => form.reset()}
                    testId="reset-changes-btn"
                  >
                    Reset changes
                  </Button>
                  <Button
                    size="sm"
                    onClick={form.submit}
                    disabled={isSimulatedClusterDemoUi}
                    testId="save-btn"
                  >
                    Save changes
                  </Button>
                </Stack>
              </FooterBox>
            </PageFooter>
          ) : null
        }
      </FormSpy>
    </RffForm>
  );
};
